import Modal from '@sdk/components/ui/modal/Modal';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import TimesheetViewModalHeader from '@web/timesheets/TimesheetViewModal/components/TimesheetViewModalHeader/TimesheetViewModalHeader';
import TimesheetViewModalEmployeeFooter from '@web/timesheets/TimesheetViewModal/components/TimesheetViewModalFooter/TimesheetViewModalEmployeeFooter/TimesheetViewModalEmployeeFooter';
import TimesheetViewModalManagerFooter from '@web/timesheets/TimesheetViewModal/components/TimesheetViewModalFooter/TimesheetViewModalManagerFooter/TimesheetViewModalManagerFooter';
import {
  ModalWrapper,
  Content,
} from '@web/timesheets/TimesheetViewModal/timesheetViewModal.styles';
import DateRow from '@web/timeClock/modals/rows/DateRow/DateRow';
import LocationRow from '@web/timeClock/modals/rows/LocationRow/LocationRow';
import PositionRow from '@web/timeClock/modals/rows/PositionRow/PositionRow';
import ScheduledRow from '@web/timeClock/modals/rows/ScheduledRow/ScheduledRow';
import ActualRow from '@web/timeClock/modals/rows/ActualRow/ActualRow';
import CommentRow from '@web/timeClock/modals/rows/CommentRow/CommentRow';
import AttestationsRow from '@web/timeClock/modals/rows/AttestationsRow/AttestationsRow';
import EditTimesheetModal from '@web/timesheets/EditTimesheetModal/EditTimesheetModal';
import { TimesheetViewProps } from '@web/timesheets/TimesheetViewModal/timesheetViewModal.types';
import { useTimesheetViewModal } from '@web/timesheets/TimesheetViewModal/timesheetViewModal.hooks';
import TimesheetHistory from '@web/timeClock/TimesheetHistory/TimesheetHistory';
import { useTimesheetHistory } from '@web/timesheets/hooks/useTimesheetHistory';
import TimesheetViewModalBanner from '@web/timesheets/TimesheetViewModal/components/TimesheetViewModalBanner';
import { getHeaderThemeColor } from '@web/timesheets/TimesheetViewModal/components/TimesheetViewModalHeader/timesheetViewModalHeader.utils';

const TimesheetViewModal = ({
  isTimesheetPreviewActive,
  shift,
  onClose,
  onTimesheetSave,
  banner,
}: TimesheetViewProps) => {
  const {
    isVisible,
    isTimesheetEditModeOn,
    setIsTimesheetEditModeOn,
    modalConfig,
    hasAnyTimesheet,
    clockInTimes,
    clockInTimesBefore,
    isAttestationVisible,
    timesheetProjectionsWithAttstations,
    timesheetEntries,
    isManager,
    isCurrentUsersTimesheet,
    hasComment,
    handleEdit,
    handleApprove,
    handleMarkPending,
    handleClose,
    isHistoryView,
    setIsHistoryView,
  } = useTimesheetViewModal({
    isTimesheetPreviewActive,
    shift,
    onClose,
    onTimesheetSave,
  });

  const { historyItems, fetchHistory } = useTimesheetHistory(shift.id, shift.user.id);

  if (isTimesheetEditModeOn) {
    return (
      <EditTimesheetModal
        isVisible={isTimesheetEditModeOn}
        onClose={() => {
          setIsTimesheetEditModeOn(false);
        }}
        onSave={({ shiftId, userId }) => {
          onTimesheetSave({ shiftId, userId });
          setIsTimesheetEditModeOn(false);
          handleClose();
        }}
        shift={shift}
      />
    );
  }

  const toggleHistoryView = () => {
    if (!isHistoryView) {
      fetchHistory();
    }

    setIsHistoryView(!isHistoryView);
  };

  const headerThemeColor = getHeaderThemeColor(shift.timesheetStatus, isManager);

  if (isHistoryView) {
    return (
      <Modal isVisible={isVisible} fixed config={modalConfig}>
        <ModalWrapper>
          <TimesheetViewModalHeader
            onClose={handleClose}
            shift={shift}
            toggleHistory={toggleHistoryView}
            isHistoryView={isHistoryView}
            showHistoryButton={Boolean(historyItems?.length)}
            themeColor={headerThemeColor}
          />
          <TimesheetHistory historyItems={historyItems} back={toggleHistoryView} />
        </ModalWrapper>
      </Modal>
    );
  }

  return (
    <Modal isVisible={isVisible} fixed config={modalConfig}>
      <ModalWrapper>
        <TimesheetViewModalHeader
          onClose={handleClose}
          shift={shift}
          toggleHistory={toggleHistoryView}
          isHistoryView={isHistoryView}
          themeColor={headerThemeColor}
          showHistoryButton={Boolean(historyItems?.length)}
        />
        <TimesheetViewModalBanner
          timesheetStatus={shift.timesheetStatus}
          isManager={isManager}
          isCurrentUsersTimesheet={isCurrentUsersTimesheet}
          timesheetEntries={timesheetEntries}
          employeeName={shift.user.fullname}
          genericBanner={banner}
        />
        <Content>
          <DateRow date={shift.dtstart} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          <LocationRow location={shift.location} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          <PositionRow position={shift.position} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          <ScheduledRow shift={shift} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          {hasAnyTimesheet && (
            <>
              <ActualRow
                shift={shift}
                clockInTimes={clockInTimes}
                clockInTimesBefore={clockInTimesBefore}
              />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
            </>
          )}
          {isAttestationVisible && (
            <>
              <AttestationsRow timesheetProjections={timesheetProjectionsWithAttstations} />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
            </>
          )}
          {hasComment && (
            <>
              <CommentRow comment={shift.assigneeNotes} />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
            </>
          )}
        </Content>
        {isManager && (
          <TimesheetViewModalManagerFooter
            onEdit={handleEdit}
            onClose={handleClose}
            onApprove={handleApprove}
            onMarkPending={handleMarkPending}
            timesheetStatus={shift.timesheetStatus}
          />
        )}
        {!isManager && <TimesheetViewModalEmployeeFooter onClose={handleClose} />}
      </ModalWrapper>
    </Modal>
  );
};

export default TimesheetViewModal;
